import { supabase } from '@/lib/supabase/config';
import { getDeeplinkPrefix } from '@/shared/utils/deeplink-prefix';

export interface InitiateProjectOwnershipTransferInput {
  projectId: number;
  toEmail: string;
}

export async function initiateProjectOwnershipTransfer({
  projectId,
  toEmail,
}: InitiateProjectOwnershipTransferInput) {
  const { data, error } = await (supabase.rpc as CallableFunction)(
    'initiate_project_ownership_transfer',
    {
      p_project_id: projectId,
      p_to_email: toEmail,
      p_deeplink_prefix: getDeeplinkPrefix(),
    },
  );

  if (error) throw error;
  return data as string;
}

export interface ResendProjectOwnershipTransferEmailInput {
  transferId: string;
}

export async function resendProjectOwnershipTransferEmail({
  transferId,
}: ResendProjectOwnershipTransferEmailInput) {
  const { data, error } = await (supabase.rpc as CallableFunction)(
    'resend_project_ownership_transfer_email',
    {
      p_transfer_id: transferId,
      p_deeplink_prefix: getDeeplinkPrefix(),
    },
  );

  if (error) throw error;
  return data as string;
}

export interface UpdateProjectOwnershipTransferEmailInput {
  transferId: string;
  newEmail: string;
}

export async function updateProjectOwnershipTransferEmail({
  transferId,
  newEmail,
}: UpdateProjectOwnershipTransferEmailInput) {
  const { data, error } = await (supabase.rpc as CallableFunction)(
    'update_project_ownership_transfer_email',
    {
      p_transfer_id: transferId,
      p_new_email: newEmail,
      p_deeplink_prefix: getDeeplinkPrefix(),
    },
  );

  if (error) throw error;
  return data as string;
}

export interface CancelProjectOwnershipTransferInput {
  transferId: string;
}

export async function cancelProjectOwnershipTransfer({
  transferId,
}: CancelProjectOwnershipTransferInput) {
  const { data, error } = await (supabase.rpc as CallableFunction)(
    'cancel_project_ownership_transfer',
    {
      p_transfer_id: transferId,
    },
  );

  if (error) throw error;
  return data as boolean;
}

export interface GetProjectOwnershipTransferInput {
  projectId: number;
}

export interface ProjectOwnershipTransfer {
  id: string;
  project_id: number;
  to_email: string;
  invitation_token: string;
  status: string;
  created_at: string;
}

export async function getProjectOwnershipTransfer({
  projectId,
}: GetProjectOwnershipTransferInput): Promise<ProjectOwnershipTransfer | null> {
  const { data, error } = await (supabase.rpc as CallableFunction)(
    'get_project_ownership_transfer',
    {
      p_project_id: projectId,
    },
  );

  if (error) throw error;
  const result = data as ProjectOwnershipTransfer[] | null;
  return result?.[0] ?? null;
}

export interface ClaimProjectOwnershipInput {
  invitationToken: string;
  orgId: number;
}

export async function claimProjectOwnership({
  invitationToken,
  orgId,
}: ClaimProjectOwnershipInput) {
  const { data, error } = await (supabase.rpc as CallableFunction)(
    'claim_project_ownership',
    {
      p_invitation_token: invitationToken,
      p_org_id: orgId,
    },
  );

  if (error) throw error;
  return data as number;
}

export interface ProjectOwnershipTransferByToken {
  id: string;
  project_id: number;
  project_name: string;
  to_email: string;
  status: string;
  inviter_name: string | null;
  inviter_email: string | null;
}

export async function getProjectOwnershipTransferByToken(
  invitationToken: string,
): Promise<ProjectOwnershipTransferByToken | null> {
  const { data, error } = await (supabase.rpc as CallableFunction)(
    'get_project_ownership_transfer_by_token',
    {
      p_invitation_token: invitationToken,
    },
  );

  if (error) throw error;
  const result = data as ProjectOwnershipTransferByToken[] | null;
  return result?.[0] ?? null;
}
